import { useEffect, useState, useMemo, useRef } from "react";
import { ACTIVITIES, AGE_GROUPS } from "@/data/parentingData";
import api from "@/lib/api";
import { getAgeBucket } from "@/lib/age";
import { useChildren } from "@/context/ChildrenContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SouvenirPhoto from "@/components/SouvenirPhoto";
import { Clock, CheckCircle2, PartyPopper, Dices, RefreshCw, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const AGE_LABEL = {
  "0-2": "Bébé · 0-2 ans",
  "3-5": "Petite enfance · 3-5 ans",
  "6-9": "Enfance · 6-9 ans",
  "10-12": "Préado · 10-12 ans",
  "13+": "Ado · 13 ans et +",
};

const ageBadge = {
  "0-2": "pastel-blush",
  "3-5": "pastel-butter",
  "6-9": "pastel-sage",
  "10-12": "pastel-sky",
  "13+": "pastel-lavender",
};

export default function ActivitiesPage() {
  const { activeChild } = useChildren();
  const [ageTab, setAgeTab] = useState("3-5");
  const [type, setType] = useState("Tous");
  const [completed, setCompleted] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [surprise, setSurprise] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);
  const targetRef = useRef(null);

  useEffect(() => {
    if (activeChild) setAgeTab(getAgeBucket(activeChild.age));
  }, [activeChild]);

  useEffect(() => {
    loadCompletions();
  }, []);

  const loadCompletions = async () => {
    const { data } = await api.get("/activities/completed");
    setCompletions(data);
    setCompleted(data.map((d) => d.activity_id));
  };

  const completionOf = (activityId) => completions.find((c) => c.activity_id === activityId);

  const pickPhoto = (completionId) => {
    targetRef.current = completionId;
    fileRef.current.value = "";
    fileRef.current.click();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    const id = targetRef.current;
    if (!file || !id) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop lourde (10 Mo maximum)");
      return;
    }
    setUploadingId(id);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/activities/${id}/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée à l'album souvenirs 📸");
      await loadCompletions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi de la photo impossible");
    } finally {
      setUploadingId(null);
      targetRef.current = null;
    }
  };

  const byAge = useMemo(() => ACTIVITIES.filter((a) => a.age === ageTab), [ageTab]);

  const types = useMemo(
    () => ["Tous", ...Array.from(new Set(byAge.map((a) => a.type))).sort((a, b) => a.localeCompare(b, "fr"))],
    [byAge]
  );

  const filtered = byAge.filter((a) => type === "Tous" || a.type === type);

  const markDone = async (a) => {
    if (completed.includes(a.id)) return;
    await api.post("/activities/complete", {
      activity_id: a.id,
      activity_title: a.title,
      child_id: activeChild?.id || null,
      child_name: activeChild?.name || null,
    });
    toast.success(`🎉 Fait ensemble : ${a.title}`);
    await loadCompletions();
  };

  const drawSurprise = () => {
    const bucket = activeChild ? getAgeBucket(activeChild.age) : ageTab;
    const pool = ACTIVITIES.filter((a) => a.age === bucket && a.id !== surprise?.id);
    if (!pool.length) return;
    setSurprise(pool[Math.floor(Math.random() * pool.length)]);
  };

  return (
    <div className="space-y-6" data-testid="activities-grid-section">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={uploadPhoto}
        className="hidden"
        data-testid="activity-photo-input"
      />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Activités ludiques
          </h1>
          <p className="mt-2 text-[#77849C]">
            {ACTIVITIES.length} idées prêtes à faire, 20 par tranche d'âge — du panier des trésors pour bébé au dîner que l'ado prépare seul.
          </p>
        </div>
        <button
          data-testid="surprise-me-btn"
          onClick={drawSurprise}
          className="brand-btn rounded-full px-5 py-2.5 flex items-center gap-2 shadow-sm hover:scale-[1.02] transition-transform duration-200"
        >
          <Dices className="h-4 w-4" />
          Surprends-moi
          {activeChild && <span className="hidden sm:inline opacity-80">· {activeChild.name}</span>}
        </button>
      </div>

      <div className="filter-row no-scrollbar" data-testid="activities-age-tabs">
        {AGE_GROUPS.map((g) => (
          <button
            key={g}
            data-testid={`activity-age-tab-${g}`}
            onClick={() => {
              setAgeTab(g);
              setType("Tous");
            }}
            className={`rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
              ageTab === g ? "bg-[#23375E] text-white" : `${ageBadge[g]} text-[#16233D] hover:brightness-95`
            }`}
          >
            {AGE_LABEL[g]}
          </button>
        ))}
      </div>

      <div className="filter-row items-center" data-testid="activities-type-filters">
        <span className="text-xs uppercase tracking-wide text-[#99A5BB] mr-1">Type</span>
        {types.map((t) => (
          <button
            key={t}
            data-testid={`type-${t}`}
            onClick={() => setType(t)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
              type === t ? "bg-[#23375E] text-white" : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
            }`}
          >
            {t}
          </button>
        ))}
        <span className="text-sm text-[#77849C] sm:ml-auto whitespace-nowrap" data-testid="activities-result-count">
          {filtered.length} activité{filtered.length > 1 ? "s" : ""}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map((a) => {
          const done = completed.includes(a.id);
          const comp = completionOf(a.id);
          return (
            <article key={a.id} data-testid={`activity-${a.id}`} className="soft-card p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-[#16233D]">{a.title}</h3>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="age-badge pastel-sage">{a.type}</span>
                    <span className="age-badge pastel-butter"><Clock className="h-3 w-3 inline mr-1" />{a.duration}</span>
                    <span className={`age-badge ${ageBadge[a.age]}`}>{a.ages}</span>
                  </div>
                </div>
                {done && <PartyPopper className="h-6 w-6 text-[#E8899F]" />}
              </div>

              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Matériel</p>
                <ul className="mt-1 text-[#44506B] text-sm">
                  {a.materials.map((m, i) => (
                    <li key={i}>· {m}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Étapes</p>
                <ol className="mt-1 space-y-1 text-[#44506B] text-sm list-decimal list-inside">
                  {a.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>

              <button data-testid={`complete-${a.id}`} onClick={() => markDone(a)}
                disabled={done}
                className={`mt-5 w-full rounded-full py-2.5 font-medium transition flex items-center justify-center gap-2 ${
                  done ? "bg-[#D2EDE1] text-[#16233D] cursor-default" : "brand-btn"
                }`}>
                {done ? <><CheckCircle2 className="h-4 w-4" /> Fait ensemble !</> : "Marquer comme fait"}
              </button>

              {done && comp && (
                comp.photo_path ? (
                  <div className="mt-3">
                    <SouvenirPhoto
                      completionId={comp.id}
                      alt={a.title}
                      className="h-40 w-full rounded-2xl object-cover"
                    />
                    <p className="mt-2 text-xs text-[#99A5BB]">Souvenir enregistré dans l'album</p>
                  </div>
                ) : (
                  <button
                    data-testid={`add-photo-${a.id}`}
                    onClick={() => pickPhoto(comp.id)}
                    disabled={uploadingId === comp.id}
                    className="mt-3 w-full rounded-full border border-[#E3E8F1] bg-white py-2.5 text-sm font-medium text-[#44506B] hover:border-[#23375E] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <ImagePlus className="h-4 w-4" />
                    {uploadingId === comp.id ? "Envoi de la photo…" : "Ajouter une photo souvenir"}
                  </button>
                )
              )}
            </article>
          );
        })}
      </div>
      <Dialog open={!!surprise} onOpenChange={(v) => !v && setSurprise(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[88vh] overflow-y-auto" data-testid="surprise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#16233D]">
              <Dices className="h-5 w-5 text-[#23375E]" />
              {activeChild ? `Pour ${activeChild.name}, aujourd'hui…` : "Votre activité surprise"}
            </DialogTitle>
            <DialogDescription>Une activité tirée au hasard pour l'âge de votre enfant.</DialogDescription>
          </DialogHeader>
          {surprise && (
            <div>
              <h3 className="text-xl font-semibold text-[#16233D]" data-testid="surprise-title">{surprise.title}</h3>
              <div className="mt-2 flex gap-2 flex-wrap">
                <span className="age-badge pastel-sage">{surprise.type}</span>
                <span className="age-badge pastel-butter"><Clock className="h-3 w-3 inline mr-1" />{surprise.duration}</span>
                <span className={`age-badge ${ageBadge[surprise.age]}`}>{surprise.ages}</span>
              </div>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Matériel</p>
                <ul className="mt-1 text-[#44506B] text-sm">
                  {surprise.materials.map((m, i) => (
                    <li key={i}>· {m}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Étapes</p>
                <ol className="mt-1 space-y-1 text-[#44506B] text-sm list-decimal list-inside">
                  {surprise.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  data-testid="surprise-redraw-btn"
                  onClick={drawSurprise}
                  className="flex-1 rounded-full border border-[#E3E8F1] bg-white py-2.5 font-medium text-[#44506B] hover:border-[#23375E] transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" /> Une autre
                </button>
                <button
                  data-testid="surprise-complete-btn"
                  onClick={() => {
                    markDone(surprise);
                    setSurprise(null);
                  }}
                  disabled={completed.includes(surprise.id)}
                  className={`flex-1 rounded-full py-2.5 font-medium ${
                    completed.includes(surprise.id) ? "bg-[#D2EDE1] text-[#16233D] cursor-default" : "brand-btn"
                  }`}
                >
                  {completed.includes(surprise.id) ? "Déjà fait !" : "On le fait !"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
